import React, { useEffect, useMemo } from 'react';
import { Box, CircularProgress, Container, Divider, Grid, Paper, Tooltip, Typography } from '@mui/material';
import TextFieldComponent from '../../common/TextFieldComponent';
import BookingDatePicker from '../../common/BookingDatePicker';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useTranslation } from 'react-i18next';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import BookingAttributionReadOnly from '../../../components/booking/BookingAttributionReadOnly';
import { attributionPayloadForEdit } from '../../../utils/costCenter';
import { useNavigate, useParams } from 'react-router';
import StatusBadge from '../../../components/StatusBadge';
import { bookingApiSlice, useFinishRentalMutation, useGetBookingDetailsQuery } from '../../../api/booking/bookingApiSlice';
import {
    CancelSaveMode,
    CancellationFeeChoice,
    useEditNonRoomBookingMutation,
    useMarkNonRoomBookingPaidMutation,
} from '../../../api/booking/bookingWriteApiSlice';
import { useAuth0 } from '../../../react-auth0-spa';
import { formatDateForDisplay } from '../../../utils';
import {
    fromStationWallClock,
    onDayKeepingTime,
    stationDateTime,
    stationPickerDay,
    stationTimeOfDay,
    toStationWallClock,
} from '../../../utils/stationTime';
import { hasBookingDetailsForOrder } from '../../../utils/bookingDetailsForOrder';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import { fetchBookingDetails, startRental } from '../../../action/bookingsCommands';
import { bookingListSelector, makeBookingSelector, makeBookingSelectorByBookingId } from '../../../selector/bookingsSelector';
import { buildBookingNumberLetterMap } from '../../../utils/bookingNumberLetters';
import ClockIcon from '../../../components/icons/ClockIcon';
import { RootState } from '../../../store/store';
import CustomButton from '../../../components/CustomButton';
import SnackbarComponent from '../../common/SnackbarComponent';
import CustomModal from '../../common/CustomModal';
import TimePickerComponent from '../../common/TimePicker';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import { useGetAssetGroupAvailabilitiesQuery } from '../../../api/group/assetGroupApiSlice';
import {
    applyDateToWindow,
    deriveAvailableDurationOptions,
    deriveAvailableStartDates,
    deriveAvailableTimeSlots,
    formatBookedWindowLabel,
    formatDurationLabel,
    shouldUseAvailabilityWindow,
    usesAvailabilityWindowForUseCase,
    withCurrentOption,
} from '../../../utils/nonRoomAvailability';
import NonRoomRentalReservationSummary from '../../../components/NonRoomRentalReservationSummary';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import { useSavedBookingPrice } from '../../../hooks/useSavedBookingPrice';
import { usePricePreview } from '../../../hooks/usePricePreview';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { useCancelBooking } from '../../../flows/cancelBookingFlow';
import { cancelOutcomeMessage } from '../../../flows/cancelOutcome';
import { useCustomerMailHint } from '../../../hooks/useCustomerMailHint';
import { useDeleteBooking } from '../../../flows/deleteBookingFlow';
import { deleteBookingErrorMessage } from '../../../utils/deleteBookingErrorMessage';
import { useIsMaster } from '../../../common/useIsMaster';
import { makeHasPermissionSelector } from '../../../selector/authenticationSelector';
import Permission from '../../../common/permissions';
import { setConfirmationDialogContent, setIsConfirmationDialog } from '../../../store/slices/globalSlice';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import CancellationFeeDialog from '../../../components/booking/CancellationFeeDialog';
import CancellationFeeInfo from '../../../components/booking/CancellationFeeInfo';
import {
    cancelErrorMessageKey,
    invoiceCorrectionMessage,
    useCancellationFeeStep,
} from '../../../hooks/useCancellationFeeStep';
import {
    makeAddIssueReportForOrderUrl,
    makeDepotDetailsUrl,
    makeRentalBookingDetailsUrl,
    paths,
} from '../../../routes';
import BookingIssueReportButton from './BookingIssueReportButton';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import RentalReturnPhotoThumbnail from './RentalReturnPhotoThumbnail';
import LockerAccessLog from './LockerAccessLog';
import ActivityTimeline from './ActivityTimeline';
import PaymentHistorySection from './PaymentHistorySection';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { CustomerMailRefusal, customerMailRefusalOf, mailBlockedOf, savedOutcomeToast } from '../../../utils/customerMailGuard';

dayjs.extend(isSameOrBefore);

// The generalized write-path (cancel / first-cut edit) covers exactly the
// self-service non-room use cases; parcel/keys keep their own lifecycle.
const EDITABLE_USE_CASES = new Set(['storage', 'rental', 'sup']);

// Build the price-preview request for the edit modal (kept out of the
// component to bound its cognitive complexity). Returns undefined while the
// inputs don't determine a price, so no request is sent (see usePricePreview).
const buildEditPreviewArgs = (params: {
    editOpen: boolean;
    useCaseIsEditable: boolean;
    useCase?: string;
    groupId?: string;
    units: number;
    startDateTime: Dayjs | null;
    endDateTime: Dayjs | null;
    discount: number;
    orderId: string;
    amountPaid: number;
}): PricePreviewArgs | undefined => {
    const { editOpen, useCaseIsEditable, useCase, groupId, units, startDateTime, endDateTime, discount, orderId, amountPaid } = params;
    if (!editOpen || !useCaseIsEditable || !useCase || !groupId || !startDateTime || !endDateTime) {
        return undefined;
    }
    return {
        useCase,
        groupId,
        checkIn: startDateTime.toISOString(),
        checkOut: endDateTime.toISOString(),
        units,
        discount,
        // Prices already-booked services from their snapshot rather than the
        // current catalog rate (#1734), and lets the breakdown report what is
        // still outstanding once a reschedule changes the total.
        orderId,
        amountPaid,
    };
};


// #3332 D3: the reason field asks about an admin price adjustment only — a
// prepaid voucher is not one, so it gets no reason field.
const adjustmentNeedingReason = (paidByPrepaidVoucher: boolean, discount: number): number =>
    paidByPrepaidVoucher ? 0 : discount;

// A typed reason travels with the save; an empty field keeps the stored one.
const reasonPayload = (reason: string): { priceAdjustmentReason?: string } =>
    reason.trim() ? { priceAdjustmentReason: reason.trim() } : {};

// A prepaid voucher is not a discount: its amount is fixed by the voucher and
// stays on the booking, so the edit dialog shows it read-only (#3332 D2, PO
// 2026-10-20). A discount on top of it is a later enhancement (plan §10 step 3).
const discountEditUnlessPrepaid = (
    paidByPrepaidVoucher: boolean,
    onDiscountChange: (newDiscount: number) => void,
): ((newDiscount: number) => void) | undefined => (paidByPrepaidVoucher ? undefined : onDiscountChange);
const BookingDetailsForNonRoomRental = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';

    const breadcrumbPathAndBookingNumber = useSelector((state: RootState) => state.bookingPage.breadcrumbPathAndBookingNumber);
    const loadingDeleteBooking = useSelector((state: RootState) => state.global.isLoading);

    const deleteBooking = useDeleteBooking();
    const cancelBooking = useCancelBooking();
    // W1-6 (#3042): a booking whose rule charges a fee is cancelled through the fee dialog.
    const feeStep = useCancellationFeeStep(orderId);
    const fetchMailHint = useCustomerMailHint();
    // Bumped every time the confirm dialog is opened, so a late-arriving
    // mail hint can tell whether it is still the dialog on screen.
    const dialogRequestRef = React.useRef(0);
    // The confirmation dialog is shared between cancel and delete — this
    // tracks which action a confirm click executes (venue-page pattern).
    const [dialogAction, setDialogAction] = React.useState<'cancel' | 'delete' | null>(null);

    // Deleting a booking is a master-only action — hidden from tenant owners
    // even though they hold the deleteBookings permission.
    const isMaster = useIsMaster();
    // Cancel + edit require the edit:bookings permission (Service holds only
    // booking VIEW, so it can view + start/finish but not cancel/edit). Matches
    // the room/venue/blocker booking-details screens.
    const hasEditBookingsPermission = useSelector(
        makeHasPermissionSelector(Permission.editBookings()),
    );
    // The problem-report permissions live in BookingIssueReportButton, which
    // owns that action end to end.
    // Drives the same skip-ending-in-days duration filter the create flow uses.
    const config = useSelector((state: RootState) => state.authentication.config);

    // Same cache entry as `BookingDetailsPage`'s query, so this subscribes
    // rather than adding a request; `refetch` keeps the post-mutation refresh.
    const { data: bookingData, refetch } = useGetBookingDetailsQuery({ orderId });

    // const booking = useSelector(makeBookingSelectorByBookingId(params.bookingId || ''));

    const booking = useSelector((state: RootState) => {
        const byId = makeBookingSelectorByBookingId(params.bookingId || '')(state);
        return byId !== null ? byId : makeBookingSelector(params.bookingId || '')(state);
    });

    // #3219: every day and time of this booking is its location's clock — the
    // booking's location from the location list, else the details payload's
    // `locationTimeZone` (the same location either way).
    const timeZone = useLocationTimeZone(booking?.locationId, bookingData?.locationTimeZone);

    // Multi-order purchases share one persisted booking number; the loaded
    // bookings list lets us derive the same display A/B/… letter the lists,
    // the end-user app and the confirmation emails show. Falls back to the
    // bare number when the sibling context isn't loaded (e.g. deep link).
    const bookingList = useSelector(bookingListSelector);
    const displayBookingNumber = useMemo(() => {
        if (!booking) return '';
        const letterMap = buildBookingNumberLetterMap(
            Object.values(bookingList),
            {
                getId: (entry) => entry.id,
                getBookingNumber: (entry) => entry.bookingNumber,
            },
        );
        return letterMap.get(booking.id) ?? booking.bookingNumber;
    }, [bookingList, booking]);

    // The recipient block renders on CONTENT, not on the object existing.
    //
    // This screen reads `booking` from the legacy Redux store, and two different
    // endpoints write that entry. The bookings list populates it on load;
    // after an edit the component dispatches `fetchBookingDetails`, whose saga
    // calls `GET order/:id` — a different handler that also returns
    // `recipientData`. So an order with a blank recipient contact row grew an
    // "Empfänger" section the moment it was edited, full of empty fields, on
    // use cases (SUP, product rental) that have no recipient at all.
    //
    // Guarding on content rather than on `recipientData &&` makes the block
    // independent of which endpoint last wrote the entry. The deeper fix — one
    // source for this screen instead of Redux and RTK Query side by side — is
    // out of scope here.
    const hasRecipientDetails = Boolean(
        booking?.recipientData
        && (booking.recipientData.firstname?.trim()
            || booking.recipientData.lastname?.trim()
            || booking.recipientData.email?.trim()
            || booking.recipientData.telephone?.trim()),
    );

    const hasBookingExpired = (): boolean => {
        if (!booking?.items[0]?.endDate || !booking?.items[0]?.startDate) {
            return true; 
        }

        const currentDateTime = new Date();
        const startTime = new Date(booking.items[0].startDate);
        const endTime = new Date(booking.items[0].endDate);

        // Disable if current time is not between start and end time
        return currentDateTime < startTime || currentDateTime > endTime;
    };

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: breadcrumbPathAndBookingNumber.path },
        { label: breadcrumbPathAndBookingNumber.bookingNumber ?? '', isActive: true },
        // { label: t('booking_details.booking'), href: paths.rentalBooking },
        // { label: data?.bookingNumber ?? '', isActive: true },
    ];

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    };

    // The booked window, written out. It used to be `end.diff(start, 'hour')`,
    // which truncates: a 15:05-16:45 booking read "1 Stunde" and a 45-minute
    // one read "0 Stunden". That is not only wrong, it HID things — the KGSt
    // booking whose window no tariff sells looked like an ordinary one-hour
    // booking on screen. One label function with the price box and the
    // duration dropdowns, so they cannot disagree (`formatBookedWindowLabel`).
    function extractHourMinute(dateInput: string | Date): string {
        const fullFormatted = formatDateForDisplay(dateInput, timeZone);
        const timePart = fullFormatted.split(' ')[1];
        return timePart;
    }

    const hasBeenStarted = Boolean(booking?.items[0]?.rental && Object.keys(booking?.items[0]?.rental).length > 0);
    const hasBeenFinished = Boolean(booking?.items[0]?.rental && !booking?.items[0]?.rental?.isActive);

    const useCaseLower = bookingData?.useCase?.toLowerCase();
    const supportsReturnPhotos = useCaseLower === 'sup' || useCaseLower === 'rental';
    // Derive the finished state from the same source as the use case (the RTK
    // Query booking details), not the separately-loaded Redux `booking`. On a
    // deep-link / refresh the Redux thunk may not have resolved yet, which
    // previously dropped the photo column even though the booking was finished.
    // Status is compared case-insensitively per the API-values convention.
    const isBookingFinished = bookingData?.status?.toLowerCase() === 'finished';
    const showReturnPhotos = supportsReturnPhotos && isBookingFinished;
    // Return-photo capture is only live for SUP today (product-rental upload
    // is still pending merge). Only there do we surface a "no photo" note for
    // a finished booking without one, so other services don't show a
    // misleading placeholder.
    const showReturnPhotoEmptyState = useCaseLower === 'sup';

    const [finishRental, { isLoading: isFinishingRental, isSuccess }] = useFinishRentalMutation();
    // Acting admin's email — sent with the finish action so booking details
    // can show who finished instead of the generic role "Admin".
    const { user } = useAuth0();
    const [snackbarData, setSnackbarData] = React.useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });

    const handleFinishBooking = async () => {
        const itemIds = booking?.items.map((item) => item.id);
        try {
            await finishRental({
                orderId: booking?.id!,
                orderItems: itemIds!,
                actorEmail: user?.email,
            }).unwrap();
            setSnackbarData({
                open: true,
                message: t('snackbar.success.finish_rental'),
                severity: 'success',
            });
            refetch(); // Refetch booking details after finishing rental
        } catch (e) {
            setSnackbarData({
                open: true,
                message: t('snackbar.error.finish_rental'),
                severity: 'error',
            });
        }
    };

    const handleStartBooking = () => {
        const itemIds = booking?.items.map((item) => item.id);
        dispatch(startRental({ orderId: booking?.id!, items: itemIds! }));
    };

    useEffect(() => {
        if (params.bookingId) {
            dispatch(fetchBookingDetails({ bookingId: params.bookingId }));
        }
    }, [params.bookingId, isSuccess]);

    const handleClickDeleteBooking = () => {
        setDialogAction('delete');
        // Invalidates any cancel-dialog mail hint still in flight, so it
        // cannot land on top of this dialog's text.
        dialogRequestRef.current += 1;
        dispatch(setIsConfirmationDialog(true));
        dispatch(
            setConfirmationDialogContent({
                content: t('booking_details.confirmDeleteDialog.content'),
                headerText: t('booking_details.confirmDeleteDialog.headerText'),
                leftButtonText: t('booking_details.confirmDeleteDialog.leftButtonText'),
                rightButtonText: t('booking_details.confirmDeleteDialog.rightButtonText'),
            }),
        );
    };

    async function handleDeleteBooking() {
        try {
            const response = await deleteBooking();
            if (response) {
                setSnackbarData({
                    open: true,
                    message: t('booking_details.delete_booking.success_message'),
                    severity: 'success',
                });
                setTimeout(() => {
                    navigate(paths.rentalBooking);
                }, 1500);
            }
        } catch (error) {
            // useDeleteBooking rethrows on a failed delete (e.g. the 403
            // master gate) — surface it instead of silently swallowing, with
            // the server's reason (deleteBookingErrorMessage).
            setSnackbarData({
                open: true,
                message: deleteBookingErrorMessage(error, t),
                severity: 'error',
            });
        }
    }

    // File a problem report for this booking. Same prefill payload the depot
    // door list already passes, so both entry points land on one add-report
    // screen; `returnTo` brings the operator back to this booking.
    const handleReportIssue = () => {
        navigate(makeAddIssueReportForOrderUrl(booking?.bookingNumber ?? orderId), {
            state: {
                locationId: booking?.locationId,
                depotId: booking?.items?.[0]?.depot?.id,
                door: booking?.items?.[0]?.doors?.[0]?.door,
                orderId: booking?.id,
                service: useCaseLower ?? '',
                bookingNumber: booking?.bookingNumber ?? '',
                returnTo: makeRentalBookingDetailsUrl(orderId),
            },
        });
    };

    // ── Soft-cancel (venue-page pattern; endpoint on the new backend) ──
    const handleClickCancelBooking = async () => {
        if (await feeStep.openIfFeeApplies()) return;
        setDialogAction('cancel');
        // Content BEFORE opening, never after. The dialog renders from the
        // store, so opening first would show whatever the last dialog left
        // there: blank on the first cancel of a page visit, and the DELETE
        // text and buttons on a page where delete was opened once — a dialog
        // reading "Buchung löschen" whose button cancels.
        const base = {
            content: t('booking_details.confirmDialog.content'),
            headerText: t('booking_details.confirmDialog.headerText'),
            leftButtonText: t('booking_details.confirmDialog.leftButtonText'),
            middleButtonText: t('booking_details.confirmDialog.middleButtonText'),
            rightButtonText: t('booking_details.confirmDialog.rightButtonText'),
        };
        dispatch(setConfirmationDialogContent(base));
        dispatch(setIsConfirmationDialog(true));

        // The mail history only enriches the question, so it arrives late
        // rather than holding the dialog shut on a slow read — a button that
        // does nothing for two seconds reads as broken.
        const request = ++dialogRequestRef.current;
        const mailHint = await fetchMailHint(orderId);
        // Dropped if anything else opened the dialog meanwhile (closed and
        // reopened as delete, say): a late write would put cancel's text over
        // another action's.
        if (!mailHint || request !== dialogRequestRef.current) return;
        dispatch(
            setConfirmationDialogContent({
                ...base,
                content: `${base.content} ${mailHint}`,
            }),
        );
    };

    async function handleCancelBooking(saveMode: CancelSaveMode, fee?: CancellationFeeChoice) {
        try {
            const response = await cancelBooking(saveMode, undefined, fee);
            feeStep.close();
            if (response) {
                const outcome = cancelOutcomeMessage(saveMode, response.mailSent);
                const invoice = invoiceCorrectionMessage(t, response.invoiceCorrection);
                setSnackbarData({
                    open: true,
                    message: invoice ? `${t(outcome.messageKey)} ${invoice.text}` : t(outcome.messageKey),
                    severity: invoice?.failed ? 'warning' : outcome.severity,
                });
                refetch();
            } else {
                setSnackbarData({
                    open: true,
                    message: t('booking_details.cancel_booking.error_message'),
                    severity: 'error',
                });
            }
        } catch (error) {
            feeStep.close();
            setSnackbarData({
                open: true,
                message: t(cancelErrorMessageKey(error)),
                severity: 'error',
            });
        }
    }

    // ── First-cut edit: dates / contact / comment (repricing = handoff, #999) ──
    const [editOpen, setEditOpen] = React.useState(false);
    // #1810: organisation (unit) and the invoice recipient's cost centre,
    // seeded when the dialog opens — the dialog owns them from then on.
    const [editAttribution, setEditAttribution] = React.useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    // The read status the dialog was SEEDED with — not the one at save time: a
    // dialog opened on a failed read holds empty fields, and a refetch that
    // succeeds meanwhile must not turn them into a "none" that wipes the
    // stored attribution.
    const [editAttributionStatus, setEditAttributionStatus] = React.useState<string | undefined>(undefined);
    const [editNonRoomBooking, { isLoading: isSavingEdit }] = useEditNonRoomBookingMutation();
    // Mark-as-paid runs against the documented `/order/:orderId/payment` contract
    // (plan §7, lead-dev hand-off) — separate from the edit mutation, which refuses
    // payment changes.
    const [markNonRoomBookingPaid, { isLoading: isSavingPayment }] = useMarkNonRoomBookingPaidMutation();
    const [editForm, setEditForm] = React.useState<{
        startDateTime: Dayjs | null;
        endDateTime: Dayjs | null;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        comment: string;
        discount: number;
        alreadyPaid: number;
    }>({ startDateTime: null, endDateTime: null, firstName: '', lastName: '', email: '', phone: '', comment: '', discount: 0, alreadyPaid: 0 });
    // #3332 D3: why the price was adjusted. Starts empty — the stored reason is
    // kept unless a new one is typed.
    const [priceAdjustmentReason, setPriceAdjustmentReason] = React.useState('');

    const useCaseIsEditable = EDITABLE_USE_CASES.has(useCaseLower ?? '');
    const usesAvailabilityWindow = usesAvailabilityWindowForUseCase(useCaseLower);
    // Deep-link to the locker (depot) backing this booking's item, so the
    // operator can jump straight to the locker overview from the booking —
    // pre-selected to this booking's door via a ?door= param (the door is
    // assigned at rental start, so it may be absent on a not-yet-started
    // booking, in which case the link just opens the depot).
    const lockerDepotId = booking?.items?.[0]?.depot?.id;
    const lockerDoor = booking?.items?.[0]?.doors?.[0]?.door;
    const lockerLink = lockerDepotId
        ? `${makeDepotDetailsUrl(lockerDepotId)}${lockerDoor ? `?door=${lockerDoor}` : ''}`
        : '';
    const bookingStatus = booking?.items[0]?.status?.toString().toLowerCase();
    // Editing and cancelling ask "has this booking started yet?", not "has it
    // been paid?". Both used to require status 'booked', which silently
    // excluded every booking with an open balance — those read 'reserved'
    // (`isBookingReserved`, #1889). So an unpaid booking, and any booking an
    // edit had just made more expensive, lost both buttons. Observed on
    // B-00000674: one edit into a pricier slot, and the booking could never be
    // edited again. The bookings most likely to need a correction were the ones
    // the UI refused to correct.
    //
    // 'reserved' is therefore allowed alongside 'booked'. Everything else stays
    // out on its own merits: 'active'/'overtime' are running (also caught by
    // `hasBeenStarted`), 'finished' and 'canceled' are over, and 'incompleted'
    // is an unpaid booking whose slot has already passed — reopening that is a
    // product question, not a display fix.
    const isOpenForChange =
        useCaseIsEditable
        && !hasBeenStarted
        && (bookingStatus === 'booked' || bookingStatus === 'reserved');
    const isCancelable = isOpenForChange;
    const isEditable = isOpenForChange;

    // Live price preview for the edit modal — the #1203 box computes the
    // breakdown from the (possibly edited) window + discount, exactly like the
    // create page. Only requested while the modal is open and the use case
    // prices; the base price is never computed in the FE.
    const editGroupId = booking?.items?.[0]?.groupId;
    const editUnits = booking?.items?.[0]?.amount ?? 1;
    const editPreviewArgs: PricePreviewArgs | undefined = useMemo(
        () => buildEditPreviewArgs({
            editOpen,
            useCaseIsEditable,
            useCase: useCaseLower,
            groupId: editGroupId,
            units: editUnits,
            startDateTime: editForm.startDateTime,
            endDateTime: editForm.endDateTime,
            discount: editForm.discount,
            orderId,
            amountPaid: bookingData?.payment?.amountPaid ?? 0,
        }),
        [
            editOpen, useCaseIsEditable, useCaseLower, editGroupId, editUnits,
            editForm.startDateTime, editForm.endDateTime, editForm.discount,
            orderId, bookingData?.payment?.amountPaid,
        ],
    );

    const { breakdown: editBreakdown, previewStatus: editPreviewStatus } = usePricePreview(editPreviewArgs);
    // No price could be determined for the edited window, so there is nothing
    // valid to save (#2036 / ADR-0018 rule 3).
    const editSaveBlocked = editPreviewStatus === 'error';
    // PO, 2026-10-30: whether the customer mail of the edited booking may go
    // out, from the backend; "save" stays available either way.
    const editMailRefusal = customerMailRefusalOf(editBreakdown?.customerMail);
    // Read-only price for the saved booking. The detail view showed no money at
    // all, so after a reschedule the new total and the amount still outstanding
    // were invisible — including the case that matters most operationally:
    // comping the increase with a discount to accommodate the change, where the
    // whole point is watching the open amount return to zero.
    //
    // Same endpoint as the edit modal, called with the booking's STORED values
    // rather than form state, so the view and the dialog can never disagree and
    // nothing is computed in the frontend (#1203).
    // The saved booking is priced by the backend from its own id (#1203): no
    // request assembled here, so the view cannot ask a different question than
    // the booking actually answers.
    const { breakdown: savedBreakdown, previewStatus: savedPreviewStatus } =
        useSavedBookingPrice(useCaseIsEditable ? orderId : undefined);
    const savedStartIso = booking?.items?.[0]?.startDate
        ? new Date(booking.items[0].startDate).toISOString()
        : null;
    const savedEndIso = booking?.items?.[0]?.endDate
        ? new Date(booking.items[0].endDate).toISOString()
        : null;
    const editStartIso = editForm.startDateTime?.toISOString() ?? null;
    const editEndIso = editForm.endDateTime?.toISOString() ?? null;

    // ── Availability-driven window selection ─────────────────────────────────
    // The dialog offers the same start slots and durations as the create flow
    // instead of free-form date/time fields: SUP and product rentals are sold
    // in fixed slots, so an arbitrary window (10:47, or an end before the
    // start) is never bookable and must not be expressible here. One date
    // picker suffices because the end is derived from the duration.
    const editDurationMinutes = useMemo(
        () => (editForm.startDateTime && editForm.endDateTime
            ? editForm.endDateTime.diff(editForm.startDateTime, 'minute')
            : 0),
        [editForm.startDateTime, editForm.endDateTime],
    );

    // Anchored on the booking's own start (not the form's) so editing the date
    // doesn't refetch, and so a booking beyond the create page's horizon still
    // gets its own day covered.
    const bookingStartIso = booking?.items?.[0]?.startDate;
    const editAvailabilityRange = useMemo(() => {
        const anchor = bookingStartIso ? dayjs(bookingStartIso) : dayjs();
        const now = dayjs();
        return {
            from: (anchor.isBefore(now) ? anchor : now).startOf('day').toISOString(),
            to: anchor.add(1, 'month').endOf('month').toISOString(),
        };
    }, [bookingStartIso]);

    const { data: editAvailabilityData, isFetching: isFetchingEditAvailability } = useGetAssetGroupAvailabilitiesQuery(
        {
            groupId: editGroupId ?? '',
            fromDate: editAvailabilityRange.from,
            toDate: editAvailabilityRange.to,
        },
        { skip: !editOpen || !editGroupId || !usesAvailabilityWindow },
    );
    const editAvailability = editAvailabilityData?.availability;

    // The form holds instants; the day and slot they mean are the station's
    // (ADR-0019), which is also how the availability payload is keyed.
    const editStartDay = useMemo(
        () => stationPickerDay(editForm.startDateTime, timeZone),
        [editForm.startDateTime, timeZone],
    );
    const editCurrentDate = editStartDay?.format('YYYY-MM-DD') ?? null;
    const editCurrentStartTime = editForm.startDateTime ? stationTimeOfDay(editForm.startDateTime, timeZone) : '';

    // Whether availability actually gave us anything to offer. Decided before
    // `withCurrentOption` adds the booking's own values back in, so an empty
    // payload reads as empty rather than as "one option".
    const availableStartDateCount = useMemo(
        () => deriveAvailableStartDates(editAvailability).length,
        [editAvailability],
    );
    const useAvailabilityWindowUi = shouldUseAvailabilityWindow({
        useCaseSupportsIt: usesAvailabilityWindow,
        isLoading: isFetchingEditAvailability,
        availableStartDateCount,
    });

    const editAvailableDates = useMemo(
        () => withCurrentOption(
            deriveAvailableStartDates(editAvailability),
            editCurrentDate,
            (option, current) => option === current,
            (a, b) => a.localeCompare(b),
        ),
        [editAvailability, editCurrentDate],
    );

    const editAvailableTimes = useMemo(
        () => withCurrentOption(
            deriveAvailableTimeSlots(editAvailability, editStartDay),
            editCurrentStartTime || null,
            (option, current) => option === current,
            (a, b) => a.localeCompare(b),
        ),
        [editAvailability, editStartDay, editCurrentStartTime],
    );

    const editDurationOptions = useMemo(
        () => withCurrentOption(
            deriveAvailableDurationOptions({
                availability: editAvailability,
                startDate: editStartDay,
                startTime: editCurrentStartTime,
                skipEndingInDays: config?.skipEndingInDays,
                timeZone,
                t,
            }),
            editDurationMinutes > 0
                ? { value: editDurationMinutes, label: formatDurationLabel(editDurationMinutes, t) }
                : null,
            (option, current) => option.value === current.value,
            (a, b) => a.value - b.value,
        ),
        [editAvailability, editStartDay, editCurrentStartTime, editDurationMinutes, config, t, timeZone],
    );

    // The end is always start + duration, so the three controls can never
    // produce a window the backend would reject as inverted.
    const applyEditWindow = (start: Dayjs | null, durationMinutes: number) => {
        setEditForm((prev) => ({
            ...prev,
            startDateTime: start,
            endDateTime: start && durationMinutes > 0 ? start.add(durationMinutes, 'minute') : null,
        }));
    };

    const handleEditDateChange = (date: Dayjs | null) => {
        // Ignores the picker's deselect (tapping the already-selected day), which
        // would otherwise clear start, end and duration in one gesture.
        const next = applyDateToWindow(date, editForm.startDateTime, editDurationMinutes, timeZone);
        if (!next) return;
        setEditForm((prev) => ({ ...prev, startDateTime: next.start, endDateTime: next.end }));
    };

    const handleEditStartTimeChange = (time: string) => {
        if (!editStartDay || !time) return;
        applyEditWindow(stationDateTime(editStartDay, time, timeZone), editDurationMinutes);
    };

    const handleEditDurationChange = (label: string) => {
        const option = editDurationOptions.find((entry) => entry.label === label);
        if (option) applyEditWindow(editForm.startDateTime, option.value);
    };

    const handleOpenEdit = () => {
        setEditForm({
            startDateTime: booking?.items[0]?.startDate ? dayjs(booking.items[0].startDate) : null,
            endDateTime: booking?.items[0]?.endDate ? dayjs(booking.items[0].endDate) : null,
            firstName: booking?.contactData?.firstname ?? '',
            lastName: booking?.contactData?.lastname ?? '',
            email: booking?.contactData?.email ?? '',
            phone: booking?.contactData?.telephone ?? '',
            comment: bookingData?.comment ?? '',
            // Prefill the current discount (stored as the archived voucher's
            // amount, surfaced by the read path as payment.couponApplied).
            discount: bookingData?.payment?.couponApplied ?? 0,
            // Prefill the amount already recorded as paid (read path: payment.amountPaid).
            alreadyPaid: bookingData?.payment?.amountPaid ?? 0,
        });
        setEditAttribution({
            organizationId: bookingData?.organizationId ?? null,
            organizationUnitId: bookingData?.organizationUnitId ?? null,
            costCenter: bookingData?.costCenter ?? '',
        });
        setEditAttributionStatus(bookingData?.attributionStatus);
        setPriceAdjustmentReason('');
        setEditOpen(true);
    };

    // Two ways out of the dialog: save quietly, or save and tell the customer.
    // Splitting them means an operator fixing a typo does not mail anyone, and
    // one who chose to send knows a mail went out — the feedback the single
    // button could not give, because it always sent.
    const handleSaveEdit = async (saveMode: 'save_only' | 'save_and_send_email') => {
        if (!editForm.startDateTime || !editForm.endDateTime || !editForm.firstName.trim() || !editForm.lastName.trim() || !editForm.email.trim()) {
            setSnackbarData({
                open: true,
                message: t('create_booking.error.missing_fields'),
                severity: 'error',
            });
            return;
        }
        // A payment is the newsworthy event, and the two mails are mutually
        // exclusive by design: the change mail deliberately stays silent for an
        // edit that did not move the window, so leaving the send with the edit
        // mutation meant a "mark paid + send" produced NO mail at all while the
        // toast said one went out (staging, 2026-09-06). The payment mail says
        // everything the change mail would have — it lists the new window too,
        // read fresh after the edit committed — so handing it the send yields
        // exactly one mail, and the more informative one.
        const paidChanged = editForm.alreadyPaid !== (bookingData?.payment?.amountPaid ?? 0);
        const wantsEmail = saveMode === 'save_and_send_email';
        try {
            const edited = await editNonRoomBooking({
                orderId,
                fromDate: editForm.startDateTime.toISOString(),
                toDate: editForm.endDateTime.toISOString(),
                customerDetails: {
                    firstName: editForm.firstName,
                    lastName: editForm.lastName,
                    email: editForm.email,
                    phone: editForm.phone,
                },
                ...attributionPayloadForEdit(editAttributionStatus, editAttribution),
                comment: editForm.comment,
                discount: editForm.discount,
                ...reasonPayload(priceAdjustmentReason),
                saveMode: paidChanged ? 'save_only' : saveMode,
            }).unwrap();
            // Record the paid amount separately (the edit mutation refuses payment
            // changes). Only when it actually changed, to avoid a needless write.
            let paymentMailSent: boolean | null = null;
            let paymentMailBlocked: CustomerMailRefusal | null = null;
            if (paidChanged) {
                const paidResult = await markNonRoomBookingPaid({
                    orderId,
                    amountPaid: editForm.alreadyPaid,
                    saveMode,
                }).unwrap();
                paymentMailSent = paidResult?.mailSent === true;
                paymentMailBlocked = mailBlockedOf(paidResult);
            }
            dispatch(bookingApiSlice.util.invalidateTags(['bookingList', 'bookingDetails']));
            dispatch(fetchBookingDetails({ bookingId: orderId }));
            setEditOpen(false);
            // Only the payment path reports back whether it mailed, so only it
            // can be contradicted. A mail the operator asked for that did not go
            // out is said out loud rather than folded into a success toast — the
            // save itself succeeded, which is why this stays `success` and the
            // wording carries the exception.
            const mailFailed = wantsEmail && paymentMailSent === false;
            let messageKey = 'booking_details.edit_booking.success_message';
            if (mailFailed) {
                messageKey = 'booking_details.edit_booking.success_message_email_failed';
            } else if (wantsEmail) {
                messageKey = 'booking_details.edit_booking.success_message_with_email';
            }
            // PO, 2026-10-30: a mail the payment rule refused names its reason.
            const mailBlocked = paymentMailBlocked ?? mailBlockedOf(edited);
            setSnackbarData({
                open: true,
                ...savedOutcomeToast(t, mailBlocked, t(messageKey)),
                ...(mailFailed && !mailBlocked ? { severity: 'warning' as const } : {}),
            });
        } catch (error: any) {
            // A capacity conflict surfaces the backend message (group fully
            // booked in the new window) instead of a generic failure.
            setSnackbarData({
                open: true,
                message: error?.data?.message || t('booking_details.edit_booking.error_message'),
                severity: 'error',
            });
        }
    };

    // Render gate: "do I hold the payload for THIS order?", never "is a request
    // in flight" — see `hasBookingDetailsForOrder`. Asked against the query
    // data, which is what this page's body renders from.
    const showBookingDetails = hasBookingDetailsForOrder(bookingData, orderId);

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            {!showBookingDetails ? (
                <Box sx={{ py: 3 }}>
                    <div className={'flex w-full h-[80vh] items-center justify-center'}>
                        <CircularProgress />
                    </div>
                </Box>
            ) : (
                <Box sx={{ py: 3 }}>
                    <PageHeader>
                        <PageHeadline>
                            {t('booking_details.title')}
                        </PageHeadline>
                    </PageHeader>
                    <div className={'mb-6'}>
                        <CustomSeparator items={breadcrumbItems} />
                    </div>
                    <Paper sx={styles.paper}>
                        {/* Header summary: 3-column flex (metadata | return photos | actions).
                            Stacks vertically below md. items-start at md+ so the metadata
                            column can grow taller than the photo / buttons without forcing
                            them to align; photo and buttons self-center vertically. */}
                        <div className={'mb-4 bg-[#F5F5F5] p-6'}>
                            <div
                                className={`
                                    flex flex-col gap-6
                                    md:grid md:grid-cols-3 md:items-start md:gap-6
                                `}
                            >
                                {/* Metadata column */}
                                <div className={'flex flex-col min-w-0 md:col-start-1'}>
                                    <div className={'flex items-center mb-2'}>
                                        <div className={'flex flex-col xl:flex-row'}>
                                            <h1 className={'text-2xl font-medium mr-4'}>{displayBookingNumber}</h1>
                                            <div className={'flex flex-row my-2 xl:my-0'}>
                                                <StatusBadge
                                                    type={booking && booking?.items[0] ? booking?.items[0].status : 'booked'}
                                                    isEditing={false}
                                                    options={[
                                                        {
                                                            label: t('booking_details.status.active'),
                                                            value: 'active',
                                                        },
                                                        {
                                                            label: t('booking_details.status.booked'),
                                                            value: 'booked',
                                                        },
                                                        {
                                                            label: t('booking_details.status.canceled'),
                                                            value: 'canceled',
                                                        },
                                                        {
                                                            label: t('booking_details.status.finished'),
                                                            value: 'finished',
                                                        },
                                                    ]}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <CancellationFeeInfo fee={bookingData?.cancellationFee} />
                                    <p className={'text-sm text-gray-500 mb-1'}>
                                        {t('booking_details.service_details')}:{' '}
                                        <span className={'font-bold'}>
                                            {bookingData?.useCase
                                                ? t(`service_type_selector.${bookingData.useCase}`)
                                                : ''
                                            }
                                        </span>
                                    </p>
                                    <p className={'text-sm text-gray-500 mb-1'}>
                                        {t('booking_details.created_on')}:{' '}
                                        <span className={'font-bold'}>{formatDateForDisplay(booking?.createdAt!, timeZone)}</span>
                                    </p>
                                    <p className={'text-sm text-gray-500 mb-1'}>
                                        {t('booking_details.last_update')}:{' '}
                                        <span className={'font-bold'}>{formatDateForDisplay(booking?.updatedAt!, timeZone)}</span>
                                    </p>
                                    {booking?.rentalStartDateTime && (
                                        <p className={'text-sm text-gray-500 mb-1'}>
                                            {t('booking_details.started_at')}:{' '}
                                            <span className={'font-bold'}>{formatDateForDisplay(booking?.rentalStartDateTime, timeZone)}</span>
                                        </p>
                                    )}
                                    {booking?.rentalEndDateTime && (
                                        <p className={'text-sm text-gray-500 mb-1'}>
                                            {t('booking_details.finished_at')}:{' '}
                                            <span className={'font-bold'}>{formatDateForDisplay(booking?.rentalEndDateTime, timeZone)}</span>
                                        </p>
                                    )}
                                    {booking?.rentalEndDateTime && (
                                        <p className={'text-sm text-gray-500 mb-1'}>
                                            {t('booking_details.finished_by')}:{' '}
                                            <span className={'font-bold'}>
                                                {booking?.items[0].rental?.finishedByEmail
                                                    ? booking.items[0].rental.finishedByEmail
                                                    : booking?.items[0].rental?.finishedBy
                                                        ? booking.items[0].rental.finishedBy.toString().charAt(0).toUpperCase()
                                                    + booking.items[0].rental.finishedBy.toString().slice(1)
                                                        : ''}
                                            </span>
                                        </p>
                                    )}
                                    <LockerAccessLog items={booking?.items ?? []} timeZone={timeZone} />
                                </div>

                                {/* Center column: return photos. Shows the photo when present,
                                    or an info placeholder for finished SUP bookings without one
                                    (e.g. the customer skipped capture). Column stays in the grid
                                    so buttons stay in col 3. */}
                                {showReturnPhotos && orderId && (
                                    <div className={'flex justify-center md:col-start-2 md:self-center'}>
                                        <RentalReturnPhotoThumbnail
                                            orderId={orderId}
                                            showEmptyState={showReturnPhotoEmptyState}
                                            timeZone={timeZone}
                                        />
                                    </div>
                                )}

                                {/* Right column: action buttons, anchored top-right */}
                                <div className={'flex flex-wrap gap-2 justify-end md:col-start-3 md:self-start'}>
                                    {lockerDepotId && (
                                        <CustomButton
                                            title={t('booking_details.buttons.go_to_locker')}
                                            onClick={() => navigate(lockerLink)}
                                            isFilled={false}
                                        />
                                    )}
                                    {/* File a problem report for this booking —
                                        prefilled with its location, depot, locker
                                        and order, the same payload the depot door
                                        list passes. Blocking the locker is NOT
                                        offered here: it is a decision about a
                                        locker and needs the door's current state,
                                        which lives on the depot door view. See
                                        docs/plans/blocking-lockers-in-use.md. */}
                                    {/* One button, four states. It must never
                                        read "Problem melden" before we know:
                                        while the lookup runs it is disabled,
                                        and if the lookup FAILS it says so
                                        rather than claiming no report exists —
                                        that would be a failure rendered as an
                                        absence, and would invite a duplicate
                                        report. */}
                                    {/* `damageReports` is the backend's own
                                        answer for this booking — the field the
                                        bookings list renders its "Problem"
                                        column from. Read, not re-derived. */}
                                    <BookingIssueReportButton
                                        orderId={orderId}
                                        bookingId={bookingData?.id ?? booking?.id}
                                        bookingNumber={bookingData?.bookingNumber ?? booking?.bookingNumber}
                                        damageReports={booking?.damageReports}
                                        onReportIssue={handleReportIssue}
                                    />
                                    {isMaster && (
                                        <CustomButton
                                            title={t('booking_details.buttons.delete_booking')}
                                            onClick={handleClickDeleteBooking}
                                            isFilled={false}
                                            variant={'delete'}
                                        />
                                    )}
                                    {isCancelable && hasEditBookingsPermission && (
                                        <CustomButton
                                            title={t('booking_details.buttons.cancel_booking')}
                                            onClick={handleClickCancelBooking}
                                            isFilled={false}
                                        />
                                    )}
                                    {isEditable && hasEditBookingsPermission && (
                                        <CustomButton
                                            title={t('booking_details.buttons.edit_button')}
                                            onClick={handleOpenEdit}
                                            isFilled={false}
                                        />
                                    )}
                                    {hasBeenStarted && !hasBeenFinished && (
                                        <CustomButton
                                            title={t('common.finish_booking')}
                                            onClick={handleFinishBooking}
                                            isFilled={false}
                                            loading={isFinishingRental}
                                            disabled={isFinishingRental}
                                        />
                                    )}
                                    {!hasBeenStarted && bookingData?.useCase !== 'parcel' && (
                                        // Start is only possible inside the booking window
                                        // (hasBookingExpired covers both before-start and
                                        // after-end). A disabled button gives no reason on
                                        // its own, so surface why via a tooltip; the span
                                        // wrapper lets the tooltip fire over the disabled
                                        // control. Empty title = no tooltip when enabled.
                                        <Tooltip
                                            title={
                                                hasBookingExpired()
                                                    ? t('booking_details.start_booking_disabled_hint')
                                                    : ''
                                            }
                                        >
                                            <span className={'inline-flex'}>
                                                <CustomButton
                                                    disabled={hasBookingExpired()}
                                                    title={t('common.start_booking')}
                                                    onClick={handleStartBooking}
                                                    isFilled
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Event-log timeline (W2.1): full open/close history
                            behind the LockerAccessLog summary above. Hidden
                            while the order has no events. */}
                        {orderId && <ActivityTimeline orderId={orderId} timeZone={timeZone} />}
                        {/* #2002: the money trail, collapsed by default.
                            Payment status stays where it is; this is the
                            detail one click away. */}
                        {orderId && <PaymentHistorySection orderId={orderId} />}
                        <Typography variant={'h3'} className={'mb-10 mt-5'}>
                            {t('create_booking.reservation_details.booking_details')}
                        </Typography>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                display: 'flex',
                                marginLeft: '0px',
                            }}
                        >
                            <Grid container spacing={2} className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}>
                                <Grid item xs={12} sm={6}>
                                    {bookingData?.useCase === 'parcel' ? (
                                        (booking?.items[0].rental?.metadata?.itemPlacementConfirmedAt) ? (
                                            <BookingDatePicker
                                                disabled={true}
                                                value={stationPickerDay(booking!.items[0].rental?.metadata.itemPlacementConfirmedAt, timeZone)}
                                                shouldDisableDate={() => false}
                                                label={t('create_booking.reservation_details.date')}
                                            />
                                        ) : booking?.reservation?.metadata?.delivery ? (
                                            <BookingDatePicker
                                                disabled={true}
                                                value={stationPickerDay(booking.reservation.metadata.delivery, timeZone)}
                                                shouldDisableDate={() => false}
                                                label={t('create_booking.reservation_details.date')}
                                            />
                                        ) : (
                                            <TextFieldComponent
                                                type={'string'}
                                                onChange={() => {}}
                                                disabled={true}
                                                value={'--'}
                                                fullWidth={true}
                                                label={t('create_booking.reservation_details.date')}
                                            />
                                        )
                                    ) : (
                                        <BookingDatePicker
                                            disabled={true}
                                            value={stationPickerDay(booking?.items[0].startDate, timeZone)}
                                            shouldDisableDate={() => false}
                                            label={t('create_booking.reservation_details.date')}
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled={true}
                                        value={
                                            bookingData?.useCase === 'parcel'
                                                ? (booking?.items[0].rental?.metadata?.itemPlacementConfirmedAt && 
                                                   booking?.items[0].rental?.metadata?.itemRetrievedConfirmedAt
                                                    ? (() => {
                                                        const start = dayjs(booking!.items[0].rental.metadata.itemPlacementConfirmedAt);
                                                        const end = dayjs(booking!.items[0].rental.metadata.itemRetrievedConfirmedAt);
                                                        const days = Math.ceil(end.diff(start, 'day', true));
                                                        return t('general.days_val', {
                                                            count: days,
                                                            defaultValue: `${days} ${days === 1 ? 'day' : 'days'}`,
                                                        });
                                                    })()
                                                    : '--')
                                                : (booking?.items[0].startDate && booking?.items[0].endDate
                                                    ? formatBookedWindowLabel(booking?.items[0].startDate, booking?.items[0].endDate, t)
                                                    : '')
                                        }
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.duration')}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled={true}
                                        value={
                                            bookingData?.useCase === 'parcel'
                                                ? (booking?.items[0].rental?.metadata?.itemPlacementConfirmedAt 
                                                    ? extractHourMinute((booking!.items[0].rental as any).metadata.itemPlacementConfirmedAt) 
                                                    : '--')
                                                : (booking?.items[0].startDate ? extractHourMinute(booking?.items[0].startDate) : '')
                                        }
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.start_time')}
                                        icon={<ClockIcon className={'size-6 text-gray-400'} />}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled={true}
                                        value={
                                            bookingData?.useCase === 'parcel'
                                                ? (booking?.items[0].rental?.metadata?.itemRetrievedConfirmedAt 
                                                    ? extractHourMinute((booking!.items[0].rental as any).metadata.itemRetrievedConfirmedAt) 
                                                    : '--')
                                                : (booking?.items[0].endDate ? extractHourMinute(booking?.items[0].endDate) : '')
                                        }
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.end_time')}
                                        icon={<ClockIcon className={'size-6 text-gray-400'} />}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled={true}
                                        value={booking && booking.locationAddress ? booking?.locationAddress.city + ', ' + booking?.locationAddress.street : ''}
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.location')}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextFieldComponent
                                        type={'string'}
                                        onChange={() => {}}
                                        disabled={true}
                                        value={
                                            bookingData?.useCase === 'parcel'
                                                ? (booking?.items[0].Group 
                                                    ? booking.items[0].Group['name'] 
                                                    : (booking?.reservation?.metadata?.group?.['name'] || '--'))
                                                : (booking?.items[0].Group ? booking?.items[0].Group['name'] : '--')
                                        }
                                        fullWidth={true}
                                        label={t('create_booking.reservation_details.booked_item')}
                                    />
                                </Grid>
                                {booking?.items?.[0]?.doors?.[0]?.door && (
                                    <Grid item xs={12} sm={6}>
                                        <TextFieldComponent
                                            type={'string'}
                                            onChange={() => {}}
                                            disabled={true}
                                            value={booking?.items[0].doors[0].door ? booking?.items[0].doors[0].door : ''}
                                            fullWidth={true}
                                            label={t('create_booking.reservation_details.assigned_locker')}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        {/* Price for the booking as saved. Read-only: the same box
                            the edit dialog uses, without the edit affordances, so
                            an operator can see the current total and what is still
                            outstanding after a reschedule — and watch it return to
                            zero when they comp the difference. */}
                        {useCaseIsEditable && (
                            <>
                                <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                                <Typography variant={'h3'} className={'mb-5'}>
                                    {t('booking_details.reservation_summary.summary')}
                                </Typography>
                                <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'}>
                                    <Grid item xs={12}>
                                        <NonRoomRentalReservationSummary
                                            breakdown={savedBreakdown}
                                            previewStatus={savedPreviewStatus}
                                            startDate={savedStartIso}
                                            timeZone={timeZone}
                                            endDate={savedEndIso}
                                            discount={bookingData?.payment?.couponApplied ?? 0}
                                            discountIsPrepaidVoucher={bookingData?.payment?.paidByPrepaidVoucher === true}
                                            amountPaid={bookingData?.payment?.amountPaid ?? 0}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}
                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <Typography variant={'h3'} className={'mb-5'}>
                            {t('create_booking.customer_details.customer')}
                        </Typography>
                        <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                            {/* First row with two Selects */}
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled={true}
                                    value={booking?.contactData?.firstname ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.first_name')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled={true}
                                    value={booking?.contactData?.lastname ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.last_name')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextFieldComponent
                                    type={'string'}
                                    onChange={() => {}}
                                    disabled={true}
                                    value={booking?.contactData?.email ?? ''}
                                    fullWidth={true}
                                    label={t('create_booking.customer_details.email')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <PhoneNumberComponent
                                    enabled={false}
                                    initialValue={booking?.contactData?.telephone ?? ''}
                                    onChange={() => {}}
                                    required={false}
                                    label={t('create_booking.customer_details.phone')}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <BookingAttributionReadOnly booking={bookingData} />
                            </Grid>
                        </Grid>
                        {
                            hasRecipientDetails && (
                                <>
                                    <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                                    <Typography variant={'h3'} className={'mb-5'}>
                                        {t('create_booking.recipient_details.recipient')}
                                    </Typography>
                                    <Grid container={true} spacing={2} className={'w-[100%] xl:w-[70%]'} sx={{ justifyContent: 'flex-start' }}>
                                        {/* First row with two Selects */}
                                        <Grid item xs={12} sm={6}>
                                            <TextFieldComponent
                                                type={'string'}
                                                onChange={() => {}}
                                                disabled={true}
                                                value={booking?.recipientData?.firstname ?? ''}
                                                fullWidth={true}
                                                label={t('create_booking.recipient_details.first_name')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextFieldComponent
                                                type={'string'}
                                                onChange={() => {}}
                                                disabled={true}
                                                value={booking?.recipientData?.lastname ?? ''}
                                                fullWidth={true}
                                                label={t('create_booking.recipient_details.last_name')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextFieldComponent
                                                type={'string'}
                                                onChange={() => {}}
                                                disabled={true}
                                                value={booking?.recipientData?.email ?? ''}
                                                fullWidth={true}
                                                label={t('create_booking.recipient_details.email')}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <PhoneNumberComponent
                                                enabled={false}
                                                initialValue={booking?.recipientData?.telephone ?? ''}
                                                onChange={() => {}}
                                                required={false}
                                                label={t('create_booking.recipient_details.phone')}
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            )
                        }
                    </Paper>
                </Box>
            )}
            <CancellationFeeDialog
                open={feeStep.open}
                quote={feeStep.quote}
                loading={loadingDeleteBooking}
                onClose={feeStep.close}
                onConfirm={handleCancelBooking}
            />
            <ConfirmationDialog
                isLoading={loadingDeleteBooking}
                handleClickConfirm={
                    dialogAction === 'cancel'
                        ? () => handleCancelBooking('save_and_send_email')
                        : handleDeleteBooking
                }
                handleClickMiddle={
                    dialogAction === 'cancel' ? () => handleCancelBooking('save_only') : undefined
                }
            />
            {/* First-cut edit dialog: dates, contact, comment. The backend
                re-runs the capacity guard on a date change and rejects
                conflicting windows; repricing stays a handoff (#999). */}
            <CustomModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title={t('booking_details.edit_booking.title')}
                description={t('booking_details.edit_booking.description')}
                saveButton={
                    <div className={'flex flex-col items-end gap-1'}>
                        {/* #2036: the admin UI must not offer what cannot be
                            priced. A settled preview error means the backend
                            could not determine a price for this window — the
                            price box already shows why — so saving is blocked
                            rather than writing a booking at an unverified
                            amount. Blocking on any settled error (not only a
                            422) is deliberate: a booking whose price we could
                            not compute must not be saved for any reason. */}
                        {editSaveBlocked && (
                            <span className={'text-xs text-foreground/60'}>
                                {t('booking_details.edit_booking.blocked_no_price')}
                            </span>
                        )}
                        <div className={'flex gap-2'}>
                            <CustomButton
                                title={t('booking_details.edit_booking.save_button')}
                                onClick={() => handleSaveEdit('save_only')}
                                loading={isSavingEdit || isSavingPayment}
                                disabled={isSavingEdit || isSavingPayment || editSaveBlocked}
                                isFilled={false}
                                fullWidth={false}
                            />
                            <CustomButton
                                title={t('booking_details.edit_booking.save_and_send_email_button')}
                                onClick={() => handleSaveEdit('save_and_send_email')}
                                loading={isSavingEdit || isSavingPayment}
                                disabled={isSavingEdit || isSavingPayment || editSaveBlocked || !!editMailRefusal}
                                isFilled={true}
                                fullWidth={false}
                            />
                        </div>
                        <CustomerMailBlockedNote refusal={editMailRefusal} />
                    </div>
                }
                closeButton={
                    <CustomButton
                        title={t('booking_details.edit_booking.cancel_button')}
                        onClick={() => setEditOpen(false)}
                        isFilled={false}
                    />
                }
            >
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {useCaseIsEditable && (
                        <Grid item xs={12}>
                            <NonRoomRentalReservationSummary
                                breakdown={editBreakdown}
                                previewStatus={editPreviewStatus}
                                startDate={editStartIso}
                                timeZone={timeZone}
                                endDate={editEndIso}
                                discount={editForm.discount}
                                discountIsPrepaidVoucher={bookingData?.payment?.paidByPrepaidVoucher === true}
                                onDiscountChange={discountEditUnlessPrepaid(
                                    bookingData?.payment?.paidByPrepaidVoucher === true,
                                    (val) => setEditForm((prev) => ({ ...prev, discount: val })),
                                )}
                                amountPaid={editForm.alreadyPaid}
                                onAlreadyPaidChange={(val) => setEditForm((prev) => ({ ...prev, alreadyPaid: val }))}
                            />
                        </Grid>
                    )}
                    {useCaseIsEditable && (
                        <PriceAdjustmentReasonField
                            discount={adjustmentNeedingReason(
                                bookingData?.payment?.paidByPrepaidVoucher === true,
                                editForm.discount,
                            )}
                            value={priceAdjustmentReason}
                            onChange={setPriceAdjustmentReason}
                        />
                    )}
                    {/* Same form, use-case-specific contents: both variants are a
                        date field followed by time fields. What can be selected
                        inside them follows what the use case actually sells. */}
                    {useAvailabilityWindowUi ? (
                        <>
                            <Grid item xs={12} sm={6}>
                                <BookingDatePicker
                                    value={editStartDay}
                                    onChange={handleEditDateChange}
                                    shouldDisableDate={() => false}
                                    availableDates={editAvailableDates}
                                    label={t('create_booking.reservation_details.start_date') + ' *'}
                                    disabled={isFetchingEditAvailability}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={editAvailableTimes}
                                    value={editCurrentStartTime}
                                    onChange={handleEditStartTimeChange}
                                    disabled={!editForm.startDateTime || isFetchingEditAvailability}
                                    loading={isFetchingEditAvailability}
                                    label={t('create_booking.reservation_details.start_time') + ' *'}
                                    noOptions={t('create_booking.reservation_details.no_times_available')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={editDurationOptions.map((option) => option.label)}
                                    value={editDurationOptions.find((option) => option.value === editDurationMinutes)?.label ?? ''}
                                    onChange={handleEditDurationChange}
                                    disabled={!editCurrentStartTime || isFetchingEditAvailability}
                                    loading={isFetchingEditAvailability}
                                    label={t('create_booking.reservation_details.duration') + ' *'}
                                    noOptions={t('create_booking.reservation_details.no_durations_available')}
                                />
                            </Grid>
                            {/* The resulting end is not repeated as a field: the
                                summary above already shows it and updates live
                                from the same derived value. */}
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} sm={6}>
                                <BookingDatePicker
                                    value={toStationWallClock(editForm.startDateTime, timeZone)}
                                    onChange={(date) => setEditForm((prev) => ({
                                        ...prev,
                                        startDateTime: onDayKeepingTime(date, prev.startDateTime, timeZone),
                                    }))}
                                    shouldDisableDate={() => false}
                                    label={t('create_booking.reservation_details.start_date') + ' *'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TimePickerComponent
                                    value={toStationWallClock(editForm.startDateTime, timeZone)}
                                    onChange={(time) => setEditForm((prev) => ({
                                        ...prev,
                                        startDateTime: fromStationWallClock(time, timeZone),
                                    }))}
                                    label={t('create_booking.reservation_details.start_time') + ' *'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <BookingDatePicker
                                    value={toStationWallClock(editForm.endDateTime, timeZone)}
                                    onChange={(date) => setEditForm((prev) => ({
                                        ...prev,
                                        endDateTime: onDayKeepingTime(date, prev.endDateTime, timeZone),
                                    }))}
                                    shouldDisableDate={() => false}
                                    label={t('create_booking.reservation_details.end_date') + ' *'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TimePickerComponent
                                    value={toStationWallClock(editForm.endDateTime, timeZone)}
                                    onChange={(time) => setEditForm((prev) => ({
                                        ...prev,
                                        endDateTime: fromStationWallClock(time, timeZone),
                                    }))}
                                    label={t('create_booking.reservation_details.end_time') + ' *'}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12} sm={6}>
                        <InputField
                            onChange={(value) => setEditForm((prev) => ({ ...prev, firstName: value }))}
                            value={editForm.firstName}
                            label={t('create_booking.customer_details.first_name') + ' *'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField
                            onChange={(value) => setEditForm((prev) => ({ ...prev, lastName: value }))}
                            value={editForm.lastName}
                            label={t('create_booking.customer_details.last_name') + ' *'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputField
                            onChange={(value) => setEditForm((prev) => ({ ...prev, email: value }))}
                            value={editForm.email}
                            label={t('create_booking.customer_details.email') + ' *'}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <PhoneNumberComponent
                            initialValue={editForm.phone}
                            onChange={(value) => setEditForm((prev) => ({ ...prev, phone: value }))}
                            required={false}
                            label={t('create_booking.customer_details.phone')}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <BookingAttributionFields
                            value={editAttribution}
                            onChange={setEditAttribution}
                            organizationLocked={bookingData?.coveredByOrg === true}
                            loadFailed={editAttributionStatus === 'failed'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <InputField
                            onChange={(value) => setEditForm((prev) => ({ ...prev, comment: value }))}
                            value={editForm.comment}
                            label={t('create_booking.customer_details.comment')}
                        />
                    </Grid>
                </Grid>
            </CustomModal>
            <SnackbarComponent
                open={snackbarData.open}
                message={snackbarData.message}
                severity={snackbarData.severity}
                onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
            />
        </Container>
    );
};

export default BookingDetailsForNonRoomRental;
